import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ComponentBase } from '../../_utils/component.base';
import { TranscriptionFlow } from '../../_utils/custom-types';
import { take } from 'rxjs';

declare let window: any;

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'custom-stream-recording',
  template: ``
})
export class CustomStreamRecordingComponent extends ComponentBase implements OnChanges {
  @Input() public socket: any;
  @Input() public socketIo: any;
  @Input() public enabledStreaming: boolean;
  @Input() public flow: TranscriptionFlow;
  @Input() selectedAudioInputDeviceId: string;
  @Output() public transcriptionHandler: EventEmitter<{ flow: string; transcription: string }>;
  @Output() public errorHandler: EventEmitter<{ flow: string; error: any }>;

  public isRecording: boolean;
  private streams: MediaStream[];
  private audioChunks: Blob[];
  private isFirstFragmentRecord: boolean;
  private transcription: { flow: string; transcription: string };
  private streamRecording: any;
  private splitTime: number;
  private startTime: number;

  constructor() {
    super();
    this.initVariables();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.socketIo?.currentValue) {
      if (this.enabledStreaming) {
        this.socketIo.on('results-streaming', (transcription: any) => {
          this.isFirstFragmentRecord = false;
          this.transcription = {flow: 'streaming', transcription};
        });
        this.socketIo.on('finish-streaming', () => {
          this.transcriptionHandler.emit(this.transcription);
          this.transcription = null;
        });
      } else {
        // Event catch after having the transcript of the user's speech (Server transcription.service.js line# 14)
        this.socketIo.on('speech-to-text-result', (transcription: any) => {
          this.transcriptionHandler.emit(transcription);
        });
        this.socketIo.on('speech-to-text-error', (error: any) => {
          this.errorHandler.emit({flow: this.flow, error});
        });
      }
    }
  }

  async checkAudioInput() {
    let errorMessage = '';
    let audioInputAvailable = false;

    try {
      // Check for media devices support
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        errorMessage = 'Media devices API not supported.';
        return { success: false, errorMessage };
      }

      // Get the list of media devices
      const devices = await navigator.mediaDevices.enumerateDevices();

      // Filter the list to get audio input devices (microphones)
      const audioInputs = devices.filter(device => device.kind === 'audioinput');

      // Check if there is at least one audio input device available
      audioInputAvailable = audioInputs.length > 0;

      if (!audioInputAvailable) {
        errorMessage = 'No audio input devices found.';
        return { success: false, errorMessage };
      }

      return { success: true };

    } catch (error) {
      audioInputAvailable = false;
      errorMessage = 'Error accessing media devices: ' + error.message;
      return { success: false, errorMessage };
    }
  }

  public async initStreaming(): Promise<void> {

    const audioConstraints: MediaTrackConstraints = {
      deviceId: this.selectedAudioInputDeviceId ? { exact: this.selectedAudioInputDeviceId } : undefined,
    };
    // make use of HTML 5/WebRTC, JavaScript getUserMedia()
    // to capture the browser microphone stream
    const stream = await navigator.mediaDevices.getUserMedia({audio: audioConstraints});
    this.streamRecording = window.RecordRTC(stream, {
      type: 'audio',
      mimeType: 'audio/wav',
      sampleRate: 44100, // this sampleRate should be the same in your server code

      // MediaStreamRecorder, StereoAudioRecorder, WebAssemblyRecorder
      // CanvasRecorder, GifRecorder, WhammyRecorder
      recorderType: window.StereoAudioRecorder,

      // Dialogflow / STT requires mono audio
      numberOfAudioChannels: 1,

      // get intervals based blobs
      // value in milliseconds
      // as you might not want to make detect calls every seconds
      timeSlice: 100,

      // only for audio track
      // audioBitsPerSecond: 128000,

      // used by StereoAudioRecorder
      // the range 22050 to 96000.
      // let us force 16khz recording:
      desiredSampRate: 44100,

      // as soon as the stream is available
      ondataavailable: (blob) => this.ondataavailable(blob)
    });
    this.streams.push(stream);
  }

  public startRecording(): void {
    this.isRecording = true;
    this.startTime = new Date().getTime();
    this.isFirstFragmentRecord = true;
    this.streamRecording.startRecording();
  }

  public stopRecording(data): void {

    const stopRecording = () => this.streamRecording.stopRecording(async () => {
    if (this.enabledStreaming) {
      this.streamTranscribeHandler();
    }
    else {
      //const dataURL = await this.getDataUrl();

      //this.socketIo.emit('speech-to-text', this.flow, {
      //   audio: {
      //     dataURL,
      //     blob: this.streamRecording.getBlob(),
      //     duration: (new Date().getTime() - this.startTime) / 1000
      //   }
      // });

      // User's audio to Avatar's audio
      this.socketIo.emit('speech-to-speech', this.flow,
        data,    // Array of messages object of the conversation + Simulation information
                 // like language, voice, LLM, attemptID and LangSmith Tags
        {
        audio: {
          //dataURL,
          language: data.settings.language ? data.settings.language.split('-')[0] : 'es',
          blob: this.streamRecording.getBlob(),
          duration: (new Date().getTime() - this.startTime) / 1000
        }
      });
    }

      this.audioChunks = [];
      this.isRecording = false;
    });

    stopRecording();
}

  public deleteStreaming(): void {
    for (const stream of this.streams) {
      stream?.getTracks()?.forEach((track: any) => {
        track.stop();
      });
    }
  }

  private streamTranscribeHandler = (): void => {
    // const streamingFragments = this.isFirstFragmentRecord ? [...this.audioChunks] : [this.signingBlob, ...this.audioChunks];
    const blob = new Blob(this.audioChunks, {type: 'audio/wav'});
    // making use of socket.io-stream for bi-directional
    // streaming, create a stream
    const stream = window.ss.createStream();
    // stream directly to server
    // it will be temp. stored locally
    window.ss(this.socket).emit('stream-transcribe', stream);
    // pipe the audio blob to the read stream
    window.ss.createBlobReadStream(blob).pipe(stream);
    console.log('stream size:', blob.size);
  };

  private getDataUrl(): Promise<string> {
    return new Promise(resolve => {
      this.streamRecording.getDataURL((audioDataURL) => {
        resolve(audioDataURL);
      });
    });
  }

  private ondataavailable = (blob: Blob): void => {
    if (!this.enabledStreaming) {
      return;
    }
    this.audioChunks.push(blob);
    const chunks = this.audioChunks.length / 10;
    if (!(chunks % this.splitTime)) {
      this.streamTranscribeHandler();
      this.audioChunks = [];
    }
  };

  private initVariables(): void {
    this.audioChunks = [];
    this.splitTime = 10;
    this.transcriptionHandler = new EventEmitter<any>();
    this.errorHandler = new EventEmitter<any>();
    this.streams = [];
    this.enabledStreaming = true;
    this.transcription = {flow: '', transcription: ''};

  }

}
